import React, { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useValuations } from '../../../hooks/administration/useValuations';
import EligibleOrdersDialog from './EligibleOrdersDialog';
import { useAuthContext } from '../../../context/AuthProvider';
import formatDate from './utils/formatDate';
import OrderStatusChip from '../../utils/components/OrderStatusChip';

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const ValuationOrdersSection = ({
  valuation,
  orders = [],
  editable = false,
}) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const {
    createDetailMutation,
    updateDetailMutation,
    deleteDetailMutation,
  } = useValuations();

  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [expandedDetails, setExpandedDetails] = useState({});
  const handleToggleDetail = (detailId) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [detailId]: !prev[detailId],
    }));
  };

  const details = useMemo(() => {
    if (Array.isArray(valuation?.details)) return valuation.details;
    return [];
  }, [valuation]);

  const totalOrders = useMemo(() => {
    return details.reduce(
      (sum, detail) => sum + (Array.isArray(detail.orders) ? detail.orders.length : 0),
      0
    );
  }, [details]);

  const totalAmount = useMemo(() => {
    return details.reduce((sum, detail) => sum + Number(detail.valdetmon || 0), 0);
  }, [details]);

  const showAlert = useCallback((message, severity = 'success') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const handleOpenCreate = () => {
    setSelectedDetail(null);
    setOpenDetailDialog(true);
  };

  const handleOpenEdit = (detail) => {
    setSelectedDetail(detail);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    if (
      createDetailMutation.isPending ||
      updateDetailMutation.isPending
    ) {
      return;
    }

    setOpenDetailDialog(false);
    setSelectedDetail(null);
  };

  const handleConfirmDetail = (payload) => {
    if (!user?.EmpID) {
      showAlert('Debe iniciar sesión.', 'error');
      return;
    }

    if (!valuation?.valcabid) {
      showAlert('Valorización inválida.', 'error');
      return;
    }

    if (selectedDetail?.valdetid) {
      updateDetailMutation.mutate(
        {
          id: valuation.valcabid,
          detailId: selectedDetail.valdetid,
          data: {
            valDetDes: payload.valDetDes,
            valDetBasCal: payload.valDetBasCal,
            orderIds: payload.orderIds,
            updbyid: user.EmpID,
          },
        },
        {
          onSuccess: () => {
            showAlert('Ítem actualizado correctamente.');
            setOpenDetailDialog(false);
            setSelectedDetail(null);
          },
          onError: (error) => {
            showAlert(
              error?.response?.data?.error ||
              error?.message ||
              'Error al actualizar el ítem.',
              'error'
            );
          },
        }
      );
      return;
    }

    createDetailMutation.mutate(
      {
        id: valuation.valcabid,
        data: {
          serProID: payload.serProID,
          valDetDes: payload.valDetDes,
          valDetBasCal: payload.valDetBasCal,
          orderIds: payload.orderIds,
          crebyid: user.EmpID,
        },
      },
      {
        onSuccess: () => {
          showAlert('Ítem creado correctamente.');
          setOpenDetailDialog(false);
          setSelectedDetail(null);
        },
        onError: (error) => {
          showAlert(
            error?.response?.data?.error ||
            error?.message ||
            'Error al crear el ítem.',
            'error'
          );
        },
      }
    );
  };

  const handleOpenDelete = (detail) => {
    setSelectedDetail(detail);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (!valuation?.valcabid || !selectedDetail?.valdetid || !user?.EmpID) return;

    deleteDetailMutation.mutate(
      {
        id: valuation.valcabid,
        detailId: selectedDetail.valdetid,
        data: { updbyid: user.EmpID },
      },
      {
        onSuccess: () => {
          showAlert('Ítem eliminado correctamente.');
          setOpenConfirmDelete(false);
          setSelectedDetail(null);
        },
        onError: (error) => {
          showAlert(
            error?.response?.data?.error ||
            error?.message ||
            'Error al eliminar el ítem.',
            'error'
          );
        },
      }
    );
  };

  const getDetailDisplayValues = (detail) => {
    const base = String(detail.valdetbascal || 'OS').toUpperCase();

    if (base === 'MANIFIESTO') {
      return {
        base,
        cantidad: Number(detail.valdetcanman || 0),
        precioUnitario: Number(detail.valdetpreuniman || 0),
        total: Number(detail.valdetmon || detail.valdetsubtot || 0),
      };
    }

    if (base === 'VIAJE_COMPLETO') {
      return {
        base,
        cantidad: Number(detail.valdetcanviacom || 0),
        precioUnitario: Number(detail.valdetpreuniviacom || 0),
        total: Number(detail.valdetmon || detail.valdetsubtot || 0),
      };
    }

    return {
      base,
      cantidad: Number(detail.valdetcan || 0),
      precioUnitario: Number(detail.valdetpreuni || 0),
      total: Number(detail.valdetmon || detail.valdetsubtot || 0),
    };
  };

  const getOrdersDateRange = (detailOrders = []) => {
    const selectedOrders = Array.isArray(detailOrders)
      ? detailOrders.filter((order) => order?.ordsercabid)
      : [];

    if (selectedOrders.length === 0) return null;

    const dates = selectedOrders
      .map((order) => order.ordsercabfec)
      .filter(Boolean)
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()));

    if (dates.length === 0) return null;

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    const from = formatDate(minDate);
    const to = formatDate(maxDate);

    return {
      from,
      to,
      sameDate: from === to,
    };
  };

  const normalizeText = (value) => String(value || '').trim().toUpperCase();

  const getManifests = (order) =>
    Array.isArray(order?.manifiestos) ? order.manifiestos : [];

  const getValorizableManifests = (order) => {
    const orderState = normalizeText(order?.ordsercabest);
    const manifests = getManifests(order);

    if (['EN_EJECUCION', 'PRE_VALORIZADA'].includes(orderState)) {
      return manifests.filter(
        (mn) => normalizeText(mn.manest) !== 'CANCELADO'
      );
    }

    return manifests.filter(
      (mn) => normalizeText(mn.manest) === 'COMPLETADO'
    );
  };

  const getCompleteTrips = (order) => {
    const valorizable = getValorizableManifests(order);

    const idas = valorizable.filter(
      (mn) => normalizeText(mn.ordsermantra) === 'IDA'
    ).length;

    const vueltas = valorizable.filter(
      (mn) => normalizeText(mn.ordsermantra) === 'VUELTA'
    ).length;

    return Math.min(idas, vueltas);
  };

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={alert.severity} onClose={handleCloseAlert}>
          {alert.message}
        </Alert>
      </Snackbar>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          mb={2.5}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Ítems de valorización
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cada ítem agrupa órdenes de servicio de un mismo tipo dentro de la valorización.
            </Typography>
          </Box>

          {editable && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: 'none' }}
            >
              Crear ítem
            </Button>
          )}
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total de ítems
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {details.length}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Órdenes asociadas
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {totalOrders}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Monto acumulado
            </Typography>
            <Typography variant="h6" fontWeight="bold" mt={1}>
              {formatCurrency(totalAmount, valuation?.monsim || 'S/')}
            </Typography>
          </Paper>
        </Stack>

        {!editable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta valorización no es editable en su estado actual. Solo puedes visualizar sus ítems y órdenes asociadas.
          </Alert>
        )}

        {details.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            No hay ítems registrados en esta valorización.
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {details.map((detail, index) => {
              const detailOrders = Array.isArray(detail.orders) ? detail.orders : [];
              const ordersDateRange = getOrdersDateRange(detailOrders);
              const display = getDetailDisplayValues(detail);
              const isExpanded = !!expandedDetails[detail.valdetid];

              const manifestsCount = detailOrders.reduce((sum, order) => {
                const manifests = Array.isArray(order.manifiestos) ? order.manifiestos : [];
                return sum + manifests.length;
              }, 0);

              const valorizableManifestsCount = detailOrders.reduce((sum, order) => {
                return sum + getValorizableManifests(order).length;
              }, 0);

              const completeTripsCount = detailOrders.reduce((sum, order) => {
                return sum + getCompleteTrips(order);
              }, 0);

              const baseLabel =
                display.base === 'MANIFIESTO'
                  ? 'Manifiestos completados'
                  : display.base === 'VIAJE_COMPLETO'
                    ? 'Viajes completos IDA/VUELTA'
                    : 'Órdenes de servicio';

              return (
                <Paper
                  key={detail.valdetid}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: '1px solid #cfd8dc',
                    borderLeft: '6px solid',
                    borderLeftColor:
                      display.base === 'MANIFIESTO'
                        ? 'secondary.main'
                        : display.base === 'VIAJE_COMPLETO'
                          ? 'warning.main'
                          : 'primary.main',
                    backgroundColor: '#fff',
                    boxShadow: '0 3px 10px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : '#f5f7fb',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      spacing={1.5}
                    >
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            label={`ITEM ${index + 1}`}
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />

                          <Typography fontWeight="bold">
                            {detail.serpronom}
                          </Typography>

                          {detail.serprouni && (
                            <Chip size="small" label={detail.serprouni} variant="outlined" />
                          )}

                          <Chip
                            size="small"
                            label={
                              display.base === 'MANIFIESTO'
                                ? 'Base: Manifiesto'
                                : display.base === 'VIAJE_COMPLETO'
                                  ? 'Base: Viaje completo'
                                  : 'Base: OS'
                            }
                            color={
                              display.base === 'MANIFIESTO'
                                ? 'secondary'
                                : display.base === 'VIAJE_COMPLETO'
                                  ? 'warning'
                                  : 'primary'
                            }
                            variant="outlined"
                          />
                        </Stack>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.8,
                            color: 'text.secondary',
                            whiteSpace: 'pre-line',
                            lineHeight: 1.55,
                          }}
                        >
                          {detail.valdetdes || 'Sin descripción'}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                        <Chip
                          size="small"
                          label={`${detailOrders.length} OS`}
                          variant="outlined"
                          color="primary"
                        />

                        <Chip
                          size="small"
                          label={`${valorizableManifestsCount}/${manifestsCount} MN`}
                          color={valorizableManifestsCount > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />

                        <Chip
                          size="small"
                          label={`${completeTripsCount} viajes`}
                          variant="outlined"
                          color={completeTripsCount > 0 ? 'warning' : 'default'}
                        />

                        <Chip
                          size="small"
                          label={`Cant. ${display.cantidad}`}
                          variant="outlined"
                          color="info"
                        />

                        <Chip
                          size="small"
                          label={`P.U. ${formatCurrency(display.precioUnitario, valuation?.monsim || 'S/')}`}
                          variant="outlined"
                          color="warning"
                        />

                        <Chip
                          size="small"
                          label={formatCurrency(display.total, valuation?.monsim || 'S/')}
                          color="success"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Stack>
                    </Stack>

                    <Box mt={1.5}>
                      <Alert severity="info" variant="outlined" sx={{ py: 0.6 }}>
                        <Typography variant="body2">
                          Este ítem se reporta usando como base: <strong>{baseLabel}</strong>.
                        </Typography>
                      </Alert>
                    </Box>

                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      mt={1.5}
                    >
                      <Box>
                        {ordersDateRange && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label={
                              ordersDateRange.sameDate
                                ? `Periodo OS: ${ordersDateRange.from}`
                                : `Periodo OS: ${ordersDateRange.from} - ${ordersDateRange.to}`
                            }
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ExpandMoreIcon />}
                          onClick={() => handleToggleDetail(detail.valdetid)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                          }}
                        >
                          {isExpanded ? 'Ocultar órdenes' : 'Ver órdenes'}
                        </Button>

                        {editable && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEdit(detail)}
                              sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                              Editar
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleOpenDelete(detail)}
                              sx={{ textTransform: 'none', borderRadius: 2 }}
                            >
                              Eliminar
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {detailOrders.length === 0 ? (
                      <Box p={2}>
                        <Alert severity="info" variant="outlined">
                          Este ítem aún no tiene órdenes asociadas.
                        </Alert>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell><strong>Código</strong></TableCell>
                              <TableCell><strong>Nombre</strong></TableCell>
                              <TableCell><strong>Fecha</strong></TableCell>
                              <TableCell align="center"><strong>#MN</strong></TableCell>
                              <TableCell align="right"><strong>Precio</strong></TableCell>
                              <TableCell><strong>Estado</strong></TableCell>
                              <TableCell align="center"><strong>Acciones</strong></TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {detailOrders.map((order) => {
                              const manifests = getManifests(order);
                              const valorizable = getValorizableManifests(order); 
                              return (
                                <TableRow key={order.ordsercabid} hover>
                                  <TableCell>
                                    <Typography fontWeight="bold" color="primary">
                                      OS-{String(order.ordsercabid).padStart(6, '0')}
                                    </Typography>
                                  </TableCell>

                                  <TableCell sx={{ maxWidth: 300 }}>
                                    <Typography noWrap title={order.ordsercabnom}>
                                      {order.ordsercabnom}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>{formatDate(order.ordsercabfec)}</TableCell>

                                  <TableCell align="center">
                                    <Chip
                                      size="small"
                                      label={`${valorizable.length}/${manifests.length}`}
                                      color={valorizable.length > 0 ? 'success' : 'default'}
                                      variant="outlined"
                                    />
                                  </TableCell>

                                  <TableCell align="right">
                                    {formatCurrency(order.ordsercabpre, valuation?.monsim || 'S/')}
                                  </TableCell>

                                  <TableCell>                                    
                                    <OrderStatusChip
                                      size="small"
                                      status={order.ordsercabest}
                                      sx={{
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell align="center">
                                    <Tooltip title="Ver orden">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          window.open(
                                            `/administracion/servicio2/${order.ordsercabid}/viajeros`,
                                            '_blank'
                                          );
                                        }}
                                      >
                                        <VisibilityIcon sx={{ color: '#009688' }} fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        )}

        <Box mt={2.5}>
          <Alert severity="info" icon={<InfoOutlinedIcon />}>
            Al eliminar un ítem completo, sus órdenes asociadas serán liberadas y volverán al estado operativo correspondiente según su avance.
          </Alert>
        </Box>
      </Paper>

      <EligibleOrdersDialog
        open={openDetailDialog}
        handleClose={handleCloseDetailDialog}
        valuation={valuation}
        detail={selectedDetail}
        onConfirm={handleConfirmDetail}
        loading={
          createDetailMutation.isPending ||
          updateDetailMutation.isPending
        }
      />

      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación de ítem</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea eliminar el ítem{' '}
            <strong>
              {selectedDetail ? `#${selectedDetail.valdetid}` : ''}
            </strong>{' '}
            de esta valorización?
          </DialogContentText>

          <Box mt={2}>
            <Alert severity="warning">
              Esta acción retirará todas las órdenes asociadas al ítem, recalculará la valorización y devolverá cada OS al estado operativo correspondiente según sus manifiestos.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteDetailMutation.isPending}
          >
            {deleteDetailMutation.isPending ? 'Eliminando...' : 'Eliminar ítem'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ValuationOrdersSection;